import {
  ChevronDown,
  ChevronRight,
  HelpCircle,
  FileText,
  Database,
  Play,
  Code,
  Zap,
  Layers,
  GitBranch,
  FileCode2,
} from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export function HowToUse() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="mt-6 border-blue-200 bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
      <CardHeader
        className="cursor-pointer pb-4 transition-all duration-200 hover:bg-blue-100/30"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center gap-3 text-blue-900">
          {isOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          <HelpCircle className="h-5 w-5" />
          Complete Guide: Prompts & Dynamic Variables
        </CardTitle>
      </CardHeader>

      {isOpen && (
        <CardContent className="pt-0 duration-200 animate-in slide-in-from-top-2">
          {/* Overview Section */}
          <div className="mb-8 rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4">
            <div className="flex items-start gap-3">
              <Layers className="mt-1 h-6 w-6 text-indigo-600" />
              <div>
                <h3 className="mb-2 text-lg font-semibold text-indigo-900">
                  System Overview
                </h3>
                <p className="text-sm leading-relaxed text-indigo-700">
                  You must first create variables before adding them to the
                  prompt.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
            {/* Prompts Section */}
            <div className="space-y-4">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-2">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-900">
                    Prompt Templates
                  </h3>
                  <p className="text-sm text-blue-600">
                    Create and manage dynamic prompts
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-blue-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-2 flex items-center gap-2 font-semibold text-gray-900">
                        <Code className="h-4 w-4" />
                        Variable Syntax
                      </h4>
                      <p className="mb-3 text-sm text-gray-700">
                        Use double-brace syntax to create dynamic variables in
                        your templates. Make sure you created the variable
                        first.
                      </p>
                      <div className="rounded-lg border bg-gray-50 p-3">
                        <code className="font-mono text-sm text-blue-700">
                          {
                            'Hello {{client_name}}, your category is {{category}}.'
                          }
                        </code>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-blue-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-sm font-bold text-white">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-2 flex items-center gap-2 font-semibold text-gray-900">
                        <GitBranch className="h-4 w-4" />
                        Version Control
                      </h4>
                      <p className="mb-3 text-sm text-gray-700">
                        Each change automatically creates a new version. Always
                        use the latest version in production.
                      </p>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className="border-green-300 text-green-700"
                        >
                          v1.0
                        </Badge>
                        <Badge
                          variant="outline"
                          className="border-blue-300 text-blue-700"
                        >
                          v2.0
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Variables Section */}
            <div className="space-y-4">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-2">
                  <Database className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-900">
                    Dynamic Variables
                  </h3>
                  <p className="text-sm text-green-600">
                    SQL queries for real-time data
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-green-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500 text-sm font-bold text-white">
                      1
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-2 flex items-center gap-2 font-semibold text-gray-900">
                        <Database className="h-4 w-4" />
                        Secure Queries
                      </h4>
                      <p className="mb-3 text-sm text-gray-700">
                        Create SELECT queries to fetch data. Use{' '}
                        <code className="rounded bg-gray-100 px-1">
                          {'{{parameter}}'}
                        </code>{' '}
                        for safe parameters calculated in their respective
                        services.
                      </p>
                      <div className="rounded-lg border bg-gray-50 p-3">
                        <code className="font-mono text-sm text-green-700">
                          SELECT name, category FROM customers WHERE id ={' '}
                          {'{{id}}'}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-green-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex items-start gap-4">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500 text-sm font-bold text-white">
                      2
                    </div>
                    <div className="flex-1">
                      <h4 className="mb-2 flex items-center gap-2 font-semibold text-gray-900">
                        <Play className="h-4 w-4" />
                        Real-Time Testing
                      </h4>
                      <p className="text-sm text-gray-700">
                        Validate your queries before saving. Instantly view
                        results and catch errors.
                      </p>
                      <div className="mt-2 rounded border-l-4 border-green-400 bg-green-50 p-2">
                        <span className="text-xs font-medium text-green-700">
                          ✅ Valid query! Returned 47 result(s).
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Implementation Guide */}
          <div className="mt-10 border-t border-blue-200 pt-8">
            <div className="mb-6 flex items-center gap-3">
              <div className="rounded-lg bg-purple-100 p-2">
                <Code className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-purple-900">
                  API Integration
                </h3>
                <p className="text-sm text-purple-600">
                  Endpoint to use in your applications
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-purple-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                <div className="mb-3 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-600" />
                  <h4 className="text-sm font-semibold text-gray-900">
                    Render Prompt with Variables
                  </h4>
                </div>
                <div className="overflow-auto rounded-lg bg-purple-50 p-3 font-mono text-xs">
                  <div className="mb-1 font-semibold text-purple-700">POST</div>
                  <div className="mb-2 text-gray-700">/v1/prompts/render</div>
                  <div className="text-gray-600">
                    JSON payload with the required parameters:
                    <br />• serviceName (string)
                    <br />• promptName (string)
                    <br />• customerId (number, optional)
                    <br />• version (string, optional - defaults to
                    &quot;latest&quot;)
                    <br />• queryParams (JSON object, optional)
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-purple-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                <div className="items-left flex flex-col gap-2">
                  <div className="mb-3 flex items-center gap-2">
                    <FileCode2 className="h-5 w-5 text-purple-600" />
                    <h4 className="text-sm font-semibold text-gray-900">
                      Payload Example
                    </h4>
                  </div>
                  <div className="w-full overflow-auto rounded-lg bg-purple-50 p-3 font-mono text-xs">
                    <pre className="text-gray-600">{` payload = {
    { 
    "serviceName": "Nico.ai",
    "promptName": "html",
    "customerId": 123,
    "version': "1.0",
    "queryParams": { "id": 2, "userId": ['101','102','103']}
    }
}`}</pre>
                  </div>
                </div>
              </div>
            </div>

            {/* Example Usage */}
            <div className="mt-8 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-blue-50 p-6">
              <h4 className="mb-3 flex items-center gap-2 font-semibold text-indigo-900">
                <Code className="h-5 w-5" />
                Practical Usage Example
              </h4>
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                <div>
                  <p className="mb-2 font-medium text-indigo-700">
                    1. Prompt Template:
                  </p>
                  <div className="rounded border bg-white p-3 font-mono text-xs">
                    {
                      'Hello {{client_name}}, we recommend products from the {{preferred_category}} category.'
                    }
                  </div>
                </div>
                <div>
                  <p className="mb-2 font-medium text-indigo-700">
                    2. Variable Query:
                  </p>
                  <div className="rounded border bg-white p-3 font-mono text-xs">
                    SELECT name, preferred_category FROM customers WHERE id ={' '}
                    {'{{id}}'}
                  </div>
                  <p className="mt-2">
                    <span className="text-xs font-medium text-indigo-700">
                      Note:
                    </span>{' '}
                    <span className="text-xs text-indigo-700">
                      {'{{id}}'} represents a queryParams: {'{"id": 123}'}
                    </span>
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <p className="mb-2 font-medium text-indigo-700">
                  3. Final Result:
                </p>
                <div className="rounded border bg-white p-3 text-sm">
                  &quot;Hello João Silva, we recommend products from the
                  Electronics category.&quot;
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
